import Image from "next/image";

import { CompareRowLabel } from "@/components/compareRowLabel/compareRowLabel.component";
import { Select } from "@/components/select/select.component";
import { Text } from "@/components/text/text.component";

import type { SanityCompareView } from "./compare.type";
import { cellToIconValue } from "./compareTableCell";
import { CompareTableIcon } from "./compareTableIcon.component";

export const CompareTableMobile = ({
  columns,
  rows,
  compareLeftId,
  compareRightId,
  compareLeftIndex,
  compareRightIndex,
  onSelectCompareLeft,
  onSelectCompareRight,
}: CompareTableMobileProps) => {
  if (!columns?.length || !rows?.length) return null;

  // We only show dual mode on mobile if there are more than 3 columns
  const dualMode = columns.length > 3;

  const leftColumn = columns.find((c) => c.id === compareLeftId);
  const rightColumn = columns.find((c) => c.id === compareRightId);

  if (dualMode && (!leftColumn || !rightColumn)) return null;

  const columnIndices = dualMode
    ? [compareLeftIndex, compareRightIndex]
    : columns.map((_, i) => i);

  const headerSlotColumns = dualMode ? [leftColumn!, rightColumn!] : columns;

  const renderDualHeader = () => (
    <div
      className="border-greyscale-strength grid grid-cols-2 gap-2 border-b px-2 pt-1 pb-4"
      role="row">
      {headerSlotColumns.map((col, slot) => (
        <div
          key={`${col.id}-${slot}`}
          className="flex flex-col items-center justify-center px-1"
          role="columnheader">
          <Image
            src={col.imageUrl as string}
            alt={col.alt?.trim() || col.title}
            width={183}
            height={183}
            className="max-w-full object-contain"
          />
          <div className="mt-2 w-full *:block *:w-full" role="cell">
            <Select
              name={
                slot === 0 ? "compare-product-left" : "compare-product-right"
              }
              aria-label={
                slot === 0
                  ? "Select first product to compare"
                  : "Select second product to compare"
              }
              value={slot === 0 ? compareLeftId : compareRightId}
              onChange={(e) =>
                slot === 0
                  ? onSelectCompareLeft(e.target.value)
                  : onSelectCompareRight(e.target.value)
              }
              containerClassName="w-full"
              className="w-full max-w-full rounded-xl py-3 pr-12 text-base">
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </Select>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAllColumnsHeader = () => (
    <div
      className="border-greyscale-strength grid gap-2 border-b px-2 pt-1 pb-4"
      role="row"
      style={{
        gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
      }}>
      {columns.map((col) => (
        <div
          key={col.id}
          className="flex flex-col items-center justify-center px-1"
          role="columnheader">
          <Image
            src={col.imageUrl as string}
            alt={col.alt?.trim() || col.title}
            width={183}
            height={183}
            className="max-w-full object-contain"
          />
          <Text
            component="div"
            variant="h5"
            className="mt-2 text-center"
            role="cell">
            {col.title}
          </Text>
        </div>
      ))}
    </div>
  );

  const renderRows = () =>
    rows.map((row) => (
      <div
        key={row.id}
        className="border-greyscale-strength border-b"
        role="row">
        <div className="py-3 pr-4 pl-5" role="rowheader">
          <CompareRowLabel label={row.label} info={row.info} variant="body4" />
        </div>
        <div
          className="grid px-2 py-3"
          style={{
            gridTemplateColumns: `repeat(${columnIndices.length}, minmax(0, 1fr))`,
          }}>
          {columnIndices.map((colIdx) => (
            <div
              role="cell"
              key={`${row.id}-${colIdx}`}
              className="flex items-center justify-center">
              <CompareTableIcon value={cellToIconValue(row.cells?.[colIdx])} />
            </div>
          ))}
        </div>
      </div>
    ));

  return (
    <div className="flex flex-col">
      {dualMode ? renderDualHeader() : renderAllColumnsHeader()}
      {renderRows()}
    </div>
  );
};

type CompareTableMobileProps = {
  columns: SanityCompareView["columns"];
  rows: SanityCompareView["rows"];
  compareLeftId: string;
  compareRightId: string;
  compareLeftIndex: number;
  compareRightIndex: number;
  onSelectCompareLeft: (columnId: string) => void;
  onSelectCompareRight: (columnId: string) => void;
};
